import {
    Box, Card, CardContent, Chip, Divider, Grid, Stack, Typography
} from "@mui/material";
import { usePageMeta } from "../hooks/usePageMeta";

interface CaseStudy {
    client: string;
    category: string;
    categoryColor: 'primary' | 'secondary' | 'success' | 'warning';
    problem: string;
    built: string;
    result: string;
    tags: string[];
    image?: string;
}

export function PortfolioPage() {
    usePageMeta({
        title: 'Portfolio',
        description: 'Case studies from ColbrenSolutions — real business problems solved with AI automation, system integration, and custom software.',
    });

    const caseStudies: CaseStudy[] = [
        {
            client: 'Auto Dealer / Fleet Client',
            category: 'System Integration',
            categoryColor: 'success',
            problem: 'Client needed to look up make, model, and current market value for vehicles by VIN number. There was no integration in place — staff were doing manual lookups and copying data by hand, which was slow and error-prone.',
            built: 'A scheduled service that runs once a day — or on demand when needed — to pull vehicle data directly from Kelley Blue Book (KBB) by VIN and store it in their database automatically.',
            result: 'Vehicle data is always current without any manual effort. Staff stopped doing lookups by hand, and the business had accurate valuations available the moment they were needed.',
            tags: ['System Integration', 'Scheduled Service', 'API Integration'],
            image: '/images/projects/noScreenshot.png',
        },
        {
            client: 'Data-Driven Business',
            category: 'System Integration',
            categoryColor: 'success',
            problem: 'The company had a reliable process for receiving data as CSV files from a 3rd-party vendor via SFTP. Their developer left, and the process continued running — until the vendor switched to dropping JSON files on an S3 bucket instead. That change broke everything and brought the client\'s data pipeline to a halt.',
            built: 'Modified the integration to read from S3 instead of SFTP, parse the new JSON format, and map the incoming data back into their existing system. No changes were required on the client\'s side — the output was identical to what their system had always expected.',
            result: 'Operations resumed immediately. The client\'s pipeline was restored with minimal disruption, and the updated integration can accommodate future vendor schema changes without a full rebuild.',
            tags: ['System Integration', 'File Processing', 'AWS S3', 'JSON Migration'],
            image: '/images/projects/noScreenshot.png',
        },
        {
            client: 'Electronics Repair Shop',
            category: 'Internal Tools',
            categoryColor: 'primary',
            problem: 'Technicians were tracking quotes, repair hours, photos, and commissions across spreadsheets and paper logs. No visibility into status, no easy way to generate customer-facing documents.',
            built: 'A web-based shop management tool. Technicians log time, upload photos, and generate customer quotes that are emailed for approval. Once approved, work orders are tracked through completion. Commission reports and payment status are calculated automatically.',
            result: 'Eliminated paper logs entirely. Quotes sent in minutes instead of days. Commission disputes dropped to zero because every hour and approval is tracked.',
            tags: ['Internal Tool', 'Document Generation', 'Automation'],
            image: '/images/projects/noScreenshot.png',
        },
        {
            client: 'Distribution / Paper Company',
            category: 'System Integration',
            categoryColor: 'success',
            problem: 'Legacy COBOL database drove order entry. When the primary system went down, the business had no way to create or look up orders — operations stopped.',
            built: 'A real-time sync layer that mirrored the COBOL data into SQL Server. Built an emergency order entry system on top of it with pick ticket and packing slip generation. Operations could continue through any outage.',
            result: 'Zero unplanned downtime impact. Staff could process orders normally regardless of mainframe status. Printed pick tickets replaced manual fulfillment coordination.',
            tags: ['System Integration', 'Database Sync', 'Reports & Documents'],
            image: '/images/projects/peninsular_small.png',
        },
        {
            client: 'US Dept. of Veterans Affairs',
            category: 'IoT & Automation',
            categoryColor: 'warning',
            problem: 'Veterans with disabilities living at home had limited support between caregiver visits. Clinicians had no visibility into daily activity patterns, medication timing, or mobility.',
            built: 'An IoT smart home platform that tracked patient location and activity throughout the home using sensors and smart devices. The system prompted patients for scheduled tasks and generated daily activity reports for clinicians and caregivers.',
            result: 'Patients maintained independence longer. Clinicians received automated daily reports without manual charting. Caregiver visit frequency was optimized based on real data.',
            tags: ['IoT', 'Automation', 'Reporting'],
            image: '/images/projects/dashboardUI_small.png',
        },
        {
            client: 'NASA — Space Shuttle Program',
            category: 'Data & Analysis Tools',
            categoryColor: 'secondary',
            problem: 'Propulsion engineers needed to analyze telemetry data from Space Shuttle tanking events across multiple launches. Data lived in separate systems. Pulling it manually and comparing trends took hours and was error-prone.',
            built: 'A telemetry retrieval and analysis tool that aggregated data from multiple sources, rendered it as overlaid plots and tables, and flagged out-of-family readings automatically. Engineers could compare data across launch events in minutes.',
            result: 'Received a NASA performance award. The tool became the standard used by the engineering community for every Shuttle launch. Analysis time dropped from hours to minutes.',
            tags: ['Data Integration', 'Analysis', 'Automation'],
            image: '/images/projects/colbren.png',
        },
        {
            client: 'NASA — Space Shuttle Program',
            category: 'Data & Analysis Tools',
            categoryColor: 'secondary',
            problem: 'Propulsion engineers navigated between multiple disconnected systems to find device specs, electrical schematics, telemetry data, and maintenance history for individual components.',
            built: 'An integrated data platform that linked all device data behind a single interface. Clicking a valve on a graphical schematic immediately surfaced all associated data — schematics, telemetry, part numbers, maintenance history — from whatever source system it lived in.',
            result: 'Received a second NASA performance award. Dramatically reduced time spent context-switching between tools during pre-launch operations.',
            tags: ['System Integration', 'Data Integration', 'Internal Tool'],
            image: '/images/projects/mpsDefects.png',
        },
        {
            client: 'Marketing Firm',
            category: 'System Integration',
            categoryColor: 'success',
            problem: 'A batch process handling hundreds of thousands of messages encoded all business logic inside massive SQL queries. The job took over 8 hours to run, was fragile and nearly impossible to debug. If anything failed after hours of processing, nothing was committed — the entire run had to restart from scratch with no way to know which data caused the failure.',
            built: 'Rewrote the service in a traditional programming language with clean, testable business logic. Integrated Azure Service Bus for reliable message delivery and used parallel processing to distribute the workload. Added structured logging so failures were immediately identifiable and the process could resume from where it left off.',
            result: 'Processing time dropped from 8+ hours to 15 minutes. Partial failures no longer required a full restart — completed work was preserved and errors were pinpointed instantly through logs.',
            tags: ['Performance', 'Service Bus', 'Parallel Processing', 'Refactor'],
            image: '/images/projects/noScreenshot.png',
        },
        {
            client: 'Sports Organization (Athlete Registration)',
            category: 'Internal Tools',
            categoryColor: 'primary',
            problem: 'A youth sports league needed profile photos of 200+ athletes during registration — before identities were confirmed on-site. Asking parents to submit photos on their own simply didn\'t work: images came in wrong formats, mismatched to athletes, or not at all.',
            built: 'An app where league staff upload the roster, select an athlete from the list, and photograph them on the spot. The image is automatically associated to the athlete\'s record, cropped to a square, resized, and uploaded — all in one step at registration.',
            result: 'All athlete photos collected accurately at registration events. No manual follow-up, no mismatched images, and no post-processing required by staff.',
            tags: ['Image Processing', 'Automation', 'Internal Tool'],
            image: '/images/projects/noScreenshot.png',
        },
        {
            client: 'Service-Oriented Business',
            category: 'Internal Tools',
            categoryColor: 'primary',
            problem: 'The business needed a simple, affordable way to manage contacts and send targeted emails. Enterprise CRM and email platforms were too expensive and too complex for what they actually needed.',
            built: 'A lightweight contact management and email marketing tool. Staff collect contacts, tag them with metadata, and send targeted emails or newsletters to selected groups. A dedicated newsletter feature was built around their weekly blog post summary. Every send is logged — who received it and when.',
            result: 'The business gained a full contact and email workflow at a fraction of the cost of third-party platforms. Targeted campaigns replaced mass blasts, and the team had complete visibility into send history.',
            tags: ['Internal Tool', 'Email Marketing', 'Automation'],
            image: '/images/projects/noScreenshot.png',
        },
        {
            client: 'Sports Organization (Youth League)',
            category: 'Internal Tools',
            categoryColor: 'primary',
            problem: 'League administrators managed athlete registration, team rosters, game schedules, and coach assignments manually across spreadsheets and email threads. Game day programs were created by hand.',
            built: 'A full league management platform covering athlete registration with compliance document tracking, roster management, schedule generation, and automated game day program creation. Sports cards generated from roster data with print-ready output.',
            result: 'Administrative time per season cut significantly. Game day programs produced in minutes rather than days. Compliance documents tracked automatically rather than chased manually.',
            tags: ['Automation', 'Document Generation', 'Internal Tool'],
            image: '/images/projects/colbrenSportsDemo.png',
        },
    ];

    return (
        <>
            <Box sx={{ mb: 5 }}>
                <Typography variant='h3' sx={{ fontWeight: 700, mb: 1 }}>Portfolio</Typography>
                <Typography variant='body1' color='text.secondary' sx={{ maxWidth: 640 }}>
                    Real problems. Real solutions. The work below spans 25+ years across industries —
                    each project is described the way a business owner would describe it, not the way an engineer would.
                </Typography>
            </Box>

            <Grid container spacing={4}>
                {caseStudies.map((cs, i) => (
                    <Grid item xs={12} key={i}>
                        <Card sx={{ boxShadow: 'none', border: '1px solid #e0e0e0', borderRadius: 2 }}>
                            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='space-between' alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
                                    <Box>
                                        <Typography variant='h6' sx={{ fontWeight: 700 }}>{cs.client}</Typography>
                                        <Chip
                                            label={cs.category}
                                            color={cs.categoryColor}
                                            size='small'
                                            sx={{ mt: 0.5 }}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: { xs: 1, sm: 0 } }}>
                                        {cs.tags.map(tag => (
                                            <Chip key={tag} label={tag} size='small' variant='outlined' sx={{ fontSize: '0.7rem' }} />
                                        ))}
                                    </Box>
                                </Stack>

                                <Divider sx={{ mb: 3 }} />

                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={4}>
                                        <Typography variant='overline' color='error' sx={{ fontWeight: 700, letterSpacing: 1 }}>
                                            The Problem
                                        </Typography>
                                        <Typography variant='body2' sx={{ mt: 0.5, lineHeight: 1.7 }}>
                                            {cs.problem}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Typography variant='overline' color='primary' sx={{ fontWeight: 700, letterSpacing: 1 }}>
                                            What I Built
                                        </Typography>
                                        <Typography variant='body2' sx={{ mt: 0.5, lineHeight: 1.7 }}>
                                            {cs.built}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Typography variant='overline' color='success.main' sx={{ fontWeight: 700, letterSpacing: 1 }}>
                                            The Result
                                        </Typography>
                                        <Typography variant='body2' sx={{ mt: 0.5, lineHeight: 1.7 }}>
                                            {cs.result}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </>
    );
};
